import i18next from 'i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isAfter, isBefore, isEqual } from 'date-fns';
import {
  addPhoneNumberPrefix,
  isPhoneNumberEmpty,
} from 'src/utils/phone';
import {
  Row,
  Option,
  CheckboxGridValue,
} from 'src/types/forms';
import './i18n';

// REGEXS
const emailRegex = /^\s*(([^<>()·=~ºªÇ¨?¿*^|#¢∞¬÷"$%"≠´}{![\]\\.,;:\s@"]+(\.[^<>·$%&/=~ºªÇ¨?¿*^|#¢∞¬÷""≠´}{!()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))\s*$/;
const alphanumericRegex = /^[a-záéíóúäëïºöüA-ZÁÉÍÓÚÄËÏÖÜ0-9 ]*$/g;
const onlyTextRegex = /^[a-zA-Z\s]*$/;
const onlyNumberRegex = /^[0-9]*$/g;
const validNumberFormat = /^[+]?\d+$/;
const validDecimalFormat = /^(\d)+((\.|,)?\d{1,2})?$/;
const userRegex = /^(?:\w|[-.@+]){1,100}$/;
export const zeroWidthSpaceRegex = /\u200B/g;

// VALIDATIONS
export const validateRequired = (message?: string) => ({
  required: {
    value: true,
    message: message || i18next.t('VALIDATIONS:REQUIRED_FIELD'),
  },
});

export const validateRequiredWithoutMessage = (isRequired: boolean) => ({
  required: {
    value: isRequired,
  },
});

export const validateRequiredPhoneNumber = (code: string) => ({
  validate: (value: string) => {
    if (!value) return false;

    return !isPhoneNumberEmpty(value, code);
  },
});

export const validateRequiredCheckboxGrid = (
  rows: Row[],
  options: Option[],
) => ({
  validate: (value: CheckboxGridValue) => {
    if (!value) return false;

    return rows.every((row) => (
      options.some((option) => {
        if (!value[row.id]) return false;
        return option.description === value[row.id][0];
      })
    ));
  },
});

export const validateEmail = () => ({
  pattern: {
    value: emailRegex,
    message: i18next.t('VALIDATIONS:INVALID_EMAIL'),
  },
});

export const validateUser = () => ({
  pattern: {
    value: userRegex,
    message: i18next.t('VALIDATIONS:INVALID_USER'),
  },
});

export const validateNumberFormat = () => ({
  pattern: {
    value: validNumberFormat,
    message: i18next.t('VALIDATIONS:INVALID_NUMBER_FIELD'),
  },
});

export const validateDecimalFormat = () => ({
  pattern: {
    value: validDecimalFormat,
    message: i18next.t('VALIDATIONS:INVALID_DECIMAL_FIELD'),
  },
});

export const validateAlphanumeric = {
  validate: (value: string) => !!value.match(alphanumericRegex) || (i18next.t('VALIDATIONS:ALPHANUMERIC') as string),
};

export const validateOnlyText = {
  validate: (value: string) => !!value.match(onlyTextRegex) || (i18next.t('VALIDATIONS:ONLY_TEXT') as string),
};

export const validatePhoneNumber = (code: string) => ({
  validate: (value: string) => {
    if (!value) return true;

    const fullCode = addPhoneNumberPrefix(code);
    const fullNumber = addPhoneNumberPrefix(value);
    const isEmpty = fullCode === fullNumber;

    return (isEmpty || isValidPhoneNumber(fullNumber))
      || (i18next.t('VALIDATIONS:INVALID_PHONE_NUMBER') as string);
  },
});

export const validateOnlyNumber = {
  validate: (value: string) => !!value.match(onlyNumberRegex) || (i18next.t('VALIDATIONS:ONLY_NUMBERS') as string),
};

export const validateMinLength = (minValue: number) => ({
  minLength: { value: minValue, message: i18next.t('VALIDATIONS:MIN_LENGTH', { count: minValue }) },
});

export const validateMaxLength = (maxValue: number) => ({
  maxLength: { value: maxValue, message: i18next.t('VALIDATIONS:MAX_LENGTH', { count: maxValue }) },
});

export const validateEqualLength = (equalValue: number) => ({
  validate: (value: string) => value.length === equalValue || (i18next.t('VALIDATIONS:EQUAL_LENGTH', { count: equalValue }) as string),
});

export const isDate = (date) => date instanceof Date && !isNaN(date as any);

export const validateIsAfterDate = (dateToCompare) => ({
  validate: (date) => {
    if (!isDate(date) || !isDate(dateToCompare) || isEqual(date, dateToCompare)) {
      return true;
    }
    return isAfter(date, dateToCompare);
  },
});

export const validateIsBeforeDate = (dateToCompare) => ({
  validate: (date) => {
    if (!isDate(date) || !isDate(dateToCompare) || isEqual(date, dateToCompare)) {
      return true;
    }
    return isBefore(date, dateToCompare);
  },
});

export const validateEqual = (equalValue, message: string) => ({
  validate: (value) => value === equalValue || message,
});

export const validateNotEqualArray = (equalValueArray, message: string) => ({
  validate: (value) => {
    let counter = 0;
    equalValueArray.forEach((item) => {
      if (item.email === value) {
        counter++;
      }
    });
    return counter < 2 || message;
  },
});

export const validateFormatDate = {
  validate: (date) => {
    if (!date) return true;
    const value = date.getDate();
    return !Number.isNaN(value) || (i18next.t('VALIDATIONS:INVALID_DATE_FIELD') as string);
  },
};

export const validateBrowserSupport = () => !!String.prototype.matchAll;
